import React, { ReactNode, useCallback, useState } from 'react';
import { Box, Thead, Tr, Th as CKTh } from '@chakra-ui/react';
import { Colors } from '@mercator-gcp/grout';
import AIMarker from 'components/elements/AIMarker';
import {
  ACTION_COLUMN_ID,
  COLUMN_WIDTHS,
  LEADS_TABLE_COLUMN_IDS,
} from 'components/elements/LeadsTable/constants';
import { FlexRow } from 'components/layout/FlexRow';
import SortableHeader from 'components/layout/Table/SortableHeader';
import { useSortedProjects } from 'hooks/useSortedProjects';
import { TABLE_HEADINGS } from 'utils/constants';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import type { TColumnProps } from 'components/elements/LeadsTable/types';

const tableHeader = {
  fontWeight: '600',
  fontSize: '10px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'brand.dark-green',
};

const stickyHeaderStyle = {
  '>tr>th:first-of-type': {
    position: 'sticky',
    left: '0',
    zIndex: Z_INDEX_DICT.LEAD_TABLE_LEFT_COLUMN_HEADER,
  },
  '>tr>th': {
    position: 'sticky',
    top: '0',
    backgroundColor: Colors.White,
    zIndex: Z_INDEX_DICT.LEAD_TABLE_HEADER,
  },
};

const workTypeRowStyle = { justifyContent: 'flex-start', gap: 'padding_4' };

const dragHandleStyle = {
  display: 'inline-flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: '2px',
  marginRight: '6px',
  opacity: 0.5,
  verticalAlign: 'middle',
  'th:hover &': { opacity: 0.9 },
};

const dragHandleRowStyle = {
  display: 'flex',
  gap: '2px',
};

const dragHandleDot = {
  width: '3px',
  height: '3px',
  borderRadius: 'full',
  backgroundColor: 'brand.dark-green',
};

const DRAG_OVER_BG = 'gray.100';

type ThProps = {
  colWidth?: string;
  children?: ReactNode;
  columnId?: string;
  isDraggable?: boolean;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent, columnId: string) => void;
  onDragOver?: (e: React.DragEvent, columnId: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, columnId: string) => void;
};

const DragHandleIcon = () => (
  <Box sx={dragHandleStyle} aria-hidden>
    <Box sx={dragHandleRowStyle}>
      <Box sx={dragHandleDot} />
      <Box sx={dragHandleDot} />
    </Box>
    <Box sx={dragHandleRowStyle}>
      <Box sx={dragHandleDot} />
      <Box sx={dragHandleDot} />
    </Box>
    <Box sx={dragHandleRowStyle}>
      <Box sx={dragHandleDot} />
      <Box sx={dragHandleDot} />
    </Box>
  </Box>
);

const Th = ({
  colWidth = 'auto',
  children,
  columnId,
  isDraggable,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: ThProps) => {
  return (
    <CKTh
      width={colWidth}
      sx={{
        ...tableHeader,
        ...(isDragOver ? { backgroundColor: DRAG_OVER_BG } : {}),
        ...(isDraggable
          ? {
              cursor: 'grab',
              userSelect: 'none',
              '&:active': { cursor: 'grabbing' },
            }
          : {}),
      }}
      draggable={isDraggable}
      onDragStart={columnId && onDragStart ? (e) => onDragStart(e, columnId) : undefined}
      onDragOver={columnId && onDragOver ? (e) => onDragOver(e, columnId) : undefined}
      onDragLeave={onDragLeave}
      onDrop={columnId && onDrop ? (e) => onDrop(e, columnId) : undefined}
      title={isDraggable ? 'Drag to reorder column' : undefined}
    >
      <FlexRow alignItems="center" display="inline-flex">
        {isDraggable ? <DragHandleIcon /> : null}
        {children}
      </FlexRow>
    </CKTh>
  );
};

type TableHeaderProps = TColumnProps & {
  visibleColumnOrder: string[];
  reorderColumns: (fromId: string, toId: string) => void;
  disableSorting?: boolean;
  showSpacerColumn?: boolean;
};

const TableHeader = ({
  visibleColumnOrder,
  reorderColumns,
  disableSorting = false,
  showSpacerColumn = false,
}: TableHeaderProps) => {
  const { heading, sortByHeading, isSorted } = useSortedProjects();
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, columnId: string) => {
    e.dataTransfer.setData('text/plain', columnId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(columnId);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as Node | null;
    const currentTarget = e.currentTarget as HTMLElement;
    if (currentTarget.contains(relatedTarget)) return;
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toId: string) => {
      e.preventDefault();
      setDragOverId(null);
      const fromId = e.dataTransfer.getData('text/plain');
      if (fromId && fromId !== toId) {
        reorderColumns(fromId, toId);
      }
    },
    [reorderColumns],
  );

  const renderHeaderContent = useCallback(
    (columnId: string): ReactNode => {
      switch (columnId) {
        case LEADS_TABLE_COLUMN_IDS.ACTION:
          return 'Action';
        case LEADS_TABLE_COLUMN_IDS.ADDRESS:
          return 'Address / City';
        case LEADS_TABLE_COLUMN_IDS.STATUS:
          return disableSorting ? (
            TABLE_HEADINGS.STATUS
          ) : (
            <SortableHeader
              onClick={() => sortByHeading(TABLE_HEADINGS.STATUS)}
              headingKey={TABLE_HEADINGS.STATUS}
              currentHeading={heading}
              isSorted={isSorted}
              label={TABLE_HEADINGS.STATUS}
            />
          );
        case LEADS_TABLE_COLUMN_IDS.LATEST_PROJECT_STAGE:
          return disableSorting ? (
            TABLE_HEADINGS.EVENT_TRIGGERS
          ) : (
            <SortableHeader
              onClick={() => sortByHeading(TABLE_HEADINGS.EVENT_TRIGGERS)}
              headingKey={TABLE_HEADINGS.EVENT_TRIGGERS}
              currentHeading={heading}
              isSorted={isSorted}
              label={TABLE_HEADINGS.EVENT_TRIGGERS}
            />
          );
        case LEADS_TABLE_COLUMN_IDS.DESCRIPTION:
          return 'Description';
        case LEADS_TABLE_COLUMN_IDS.MY_NOTES:
          return 'Latest Note';
        case LEADS_TABLE_COLUMN_IDS.TOTAL_VALUE:
          return disableSorting ? (
            'Total Value'
          ) : (
            <SortableHeader
              onClick={() => sortByHeading(TABLE_HEADINGS.COST)}
              headingKey={TABLE_HEADINGS.COST}
              currentHeading={heading}
              isSorted={isSorted}
              label="Total Value"
            />
          );
        case LEADS_TABLE_COLUMN_IDS.WORK_TYPE:
          return (
            <FlexRow sx={workTypeRowStyle}>
              Work Type
              <AIMarker />
            </FlexRow>
          );
        case LEADS_TABLE_COLUMN_IDS.LOT_SIZE:
          return 'Lot Size';
        case LEADS_TABLE_COLUMN_IDS.SECTORS:
          return disableSorting ? (
            TABLE_HEADINGS.SECTORS
          ) : (
            <SortableHeader
              onClick={() => sortByHeading(TABLE_HEADINGS.SECTORS)}
              headingKey={TABLE_HEADINGS.SECTORS}
              currentHeading={heading}
              isSorted={isSorted}
              label={TABLE_HEADINGS.SECTORS}
            />
          );
        case LEADS_TABLE_COLUMN_IDS.COMPANY:
          return disableSorting ? (
            TABLE_HEADINGS.LATEST_COMPANY
          ) : (
            <SortableHeader
              onClick={() => sortByHeading(TABLE_HEADINGS.LATEST_COMPANY)}
              headingKey={TABLE_HEADINGS.LATEST_COMPANY}
              currentHeading={heading}
              isSorted={isSorted}
              label={TABLE_HEADINGS.LATEST_COMPANY}
            />
          );
        case LEADS_TABLE_COLUMN_IDS.LAST_UPDATED:
          return disableSorting ? (
            TABLE_HEADINGS.LAST_UPDATE
          ) : (
            <SortableHeader
              onClick={() => sortByHeading(TABLE_HEADINGS.LAST_UPDATE)}
              headingKey={TABLE_HEADINGS.LAST_UPDATE}
              currentHeading={heading}
              isSorted={isSorted}
              label={TABLE_HEADINGS.LAST_UPDATE}
            />
          );
        case LEADS_TABLE_COLUMN_IDS.DATE_CREATED:
          return disableSorting ? (
            TABLE_HEADINGS.DATE_CREATED
          ) : (
            <SortableHeader
              onClick={() => sortByHeading(TABLE_HEADINGS.DATE_CREATED)}
              headingKey={TABLE_HEADINGS.DATE_CREATED}
              currentHeading={heading}
              isSorted={isSorted}
              label={TABLE_HEADINGS.DATE_CREATED}
            />
          );
        default:
          return null;
      }
    },
    [disableSorting, heading, isSorted, sortByHeading],
  );

  return (
    <Thead sx={stickyHeaderStyle}>
      <Tr>
        {visibleColumnOrder.map((columnId) => (
          <Th
            key={columnId}
            colWidth={COLUMN_WIDTHS[columnId]}
            columnId={columnId}
            isDraggable={columnId !== ACTION_COLUMN_ID}
            isDragOver={dragOverId === columnId}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {renderHeaderContent(columnId)}
          </Th>
        ))}
        {showSpacerColumn && <Th colWidth="auto" />}
      </Tr>
    </Thead>
  );
};

export default TableHeader;
